<template>
  <div>
    <div class="py-2">
      <a
        class="text-decoration-none bold back-nav d-flex align-items-center"
        href="#"
        @click="$emit('back')"
        v-if="showBackButton"
        ><i class="caret-left-icon ml-n1"></i> <span class="ml-1">Back</span></a
      >
    </div>
    <header class="text-extra-large bold py-2 text-ellipsis">
      {{ customField ? 'Edit' : 'New' }} Custom field
      {{ customField ? ` - ${customField.name}` : '' }}
    </header>
    <template v-if="customField">
      <div v-if="customField.projectId">
        Changes will be applied to all Initiatives that use this Custom Field.
      </div>
      <div class="notification-box mt-3 p-3" v-else>
        You're modifying a custom field from the <b>Org library</b>. These changes will be applied
        to all Initiatives that use this Custom Field.
      </div>
    </template>
    <div v-else-if="!projectId">This custom field will be available to everyone in your Org.</div>
    <main class="col-9 px-0 pt-4">
      <div class="field">
        <label class="bold" for="fieldType">Field type</label>
        <b-dropdown
          :disabled="mode === 'EDIT'"
          id="fieldType"
          class="d-block ml-n2"
          variant="outline-default"
          menu-class="menu-shadow scrollable text-medium"
          toggle-class="rounded-pill dropdown-field text-truncate text-medium"
        >
          <template slot="button-content">
            <span>
              {{ fieldType.name }}
            </span>
          </template>
          <b-dropdown-item
            :active="fieldType.value === field.value"
            v-for="field in fieldTypes"
            :key="field.value"
            @click="fieldType = field"
            >{{ field.name }}
          </b-dropdown-item>
        </b-dropdown>
        <b-form-invalid-feedback :state="errors.fieldType">
          {{ errors.fieldType && errors.fieldType[0] }}
        </b-form-invalid-feedback>
      </div>
      <div class="field">
        <label class="bold" for="fieldName">Field Name</label>
        <div class="ml-n2">
          <b-form-input
            class="text-medium"
            v-model="fieldName"
            :state="errors.name ? false : null"
            id="fieldName"
            :autofocus="true"
            autocomplete="off"
          ></b-form-input>
          <b-form-invalid-feedback :state="!!errors.name" class="ml-2">
            {{ errors.name && errors.name[0] }}
          </b-form-invalid-feedback>
        </div>
      </div>
      <div class="field" v-if="fieldType.value === CUSTOM_FIELD_TYPE.DROPDOWN">
        <label class="bold">Dropdown options</label>
        <div class="dropdown-options" ref="optionsContainer">
          <div
            class="dropdown-option-field"
            v-for="(option, index) in dropdownOptions"
            :key="option.id"
            :data-id="option.id"
          >
            <div class="d-flex">
              <i class="gripper-icon" v-if="dropdownOptions.length > 1"></i>
              <div class="col-7 px-0">
                <b-form-input
                  :placeholder="'Option ' + (index + 1)"
                  class="text-medium"
                  v-model="option.name"
                  :state="getDropdownOptionsNameError(index) ? false : null"
                  ref="option"
                ></b-form-input>
                <b-form-invalid-feedback :state="getDropdownOptionsNameError(index)" class="ml-2">
                  {{ getDropdownOptionsNameError(index) }}
                </b-form-invalid-feedback>
              </div>
              <color-picker v-model="option.color" class="ml-2" :allow-reset="true" />
              <icon-dropdown
                v-if="dropdownOptions.length > 1"
                class="options-dd"
                :options="[{ label: 'Delete', id: 'delete' }]"
                @change="triggerOptionDeletion(index)"
              />
            </div>
          </div>
        </div>
        <b-button
          variant="link"
          class="text-medium text-primary px-0 mt-2 d-block"
          @click="addDropdownOption"
          >Add a dropdown option
        </b-button>
      </div>
      <div
        class="field"
        v-if="isComputedFieldsAllowed && fieldType.value === CUSTOM_FIELD_TYPE.NUMBER"
      >
        <div v-if="showFormulaField">
          <label class="bold" for="fieldName">Formula</label>
          <div class="ml-n2">
            <cf-formula-auto-complete-list
              :custom-field="customField"
              :org-id="orgId"
              :project-id="projectId"
              v-model="formula"
              ref="formulaField"
            />
          </div>
        </div>
        <link-button class="px-0" @click="addFormulaField" v-else-if="mode === 'CREATE'" underline
          >+ Add formula
        </link-button>
      </div>
      <b-form-checkbox
        v-model="addToOrg"
        :disabled="mode === 'EDIT' || !projectId"
        name="checkbox-1"
        :value="true"
        :unchecked-value="false"
      >
        Add this field to <b>{{ orgName }}'s field library</b> for anyone to use
      </b-form-checkbox>
      <div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>
    </main>
    <footer class="d-flex justify-content-between align-items-center">
      <submit-buttons
        :is-loading="isSubmitting"
        @save="onSubmit"
        @cancel="$emit('back')"
        :submitText="customField ? 'Save Changes' : 'Create'"
      />
      <b-button
        variant="link text-error"
        class="text-secondary text-medium"
        @click="triggerDeleteConfirmation"
        v-if="mode === 'EDIT'"
        >Delete
      </b-button>
    </footer>
    <delete-cf-confirm-dialog
      @deleted="$emit('deleted', customField)"
      ref="confirmModal"
      :custom-field="customField"
      :org-id="orgId"
      v-if="mode === 'EDIT'"
    />
    <confirm-dialog
      ref="optionDeleteConfirmModal"
      title="Delete this dropdown option?"
      @ok="removeDropdownOption"
      ok-title="Yes, I understand, delete."
      ok-variant="danger"
      cancel-title="No, I changed my mind"
      v-if="mode === 'EDIT'"
    >
      <p>
        This dropdown option may be in use, deleting this will remove it from ALL the initiatives
        that may be using it.
        <b>This action cannot be undone.</b>
      </p>
    </confirm-dialog>
  </div>
</template>

<script>
import LinkButton from '@/components/buttons/LinkButton.vue'
import ConfirmDialog from '@/components/ConfirmDialog'
import CfFormulaAutoCompleteList from '@/components/customFields/CfFormulaAutoCompleteList.vue'
import IconDropdown from '@/components/IconDropdown'
import ColorPicker from '@/components/initiative/ColorPicker.vue'
import DeleteCfConfirmDialog from '@/components/initiative/DeleteCfConfirmDialog'
import SubmitButtons from '@/components/SubmitButtons'
import { COLORS } from '@/constants/common'
import { CUSTOM_FIELD_LABEL, CUSTOM_FIELD_TYPE } from '@/constants/customFields'
import events from '@/constants/events'
import { featureFlags } from '@/constants/featureFlags'
import { DEFAULT_ERROR } from '@/constants/messages'
import DragAndDropMixin from '@/mixins/DragAndDropMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import ListNavigationMixin from '@/mixins/ListNavigationMixin'
import service from '@/service'
import urlManager from '@/urlManager'
import {
  BButton,
  BDropdown,
  BDropdownItem,
  BFormCheckbox,
  BFormInput,
  BFormInvalidFeedback
} from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'

const fieldTypes = Object.values(CUSTOM_FIELD_TYPE).map(fieldValue => ({
  value: fieldValue,
  name: CUSTOM_FIELD_LABEL[fieldValue]
}))

/**
 * This component can be used to create/edit custom field
 * props
 *  - customField - This required while editing a custom field
 *  - orgId
 *
 *  @emits
 *  - saved - Emitted when custom field is created/saved
 *  - deleted - Emitted when custom field is deleted
 */
export default {
  name: 'CustomFieldEditor',
  props: [
    'customField',
    'orgId',
    'projectId',
    'showBackButton',
    'teamId',
    'enableAfterCreation',
    'displayAfterCreation'
  ],
  mixins: [DragAndDropMixin, ListNavigationMixin, EventBusMixin, FeaturesMixin],
  data() {
    let dropdownOptions = [this.getNewOption(COLORS[0]), this.getNewOption(COLORS[1])]
    if (this.customField && this.customField.type === CUSTOM_FIELD_TYPE.DROPDOWN) {
      dropdownOptions = this.customField.dropdownOptions
    }
    const mode = this.customField ? 'EDIT' : 'CREATE'
    return {
      errors: {},
      errorMessage: null,
      fieldType: fieldTypes[0],
      fieldName: null,
      formula: null,
      dropdownOptions,
      fieldTypes,
      CUSTOM_FIELD_TYPE,
      indexToDelete: null,
      orgName: this.$store.getters.orgById(this.orgId).name,
      addToOrg: mode === 'CREATE' ? !this.projectId : !this.customField.projectId,
      isSubmitting: false,
      showFormulaField: !!this.customField?.formula,
      isComputedFieldsAllowed: this.getFeatureFlagValue(
        this.orgId,
        featureFlags.ALLOW_COMPUTED_FIELDS
      ),
      mode
    }
  },
  components: {
    CfFormulaAutoCompleteList,
    LinkButton,
    BFormCheckbox,
    BFormInput,
    BFormInvalidFeedback,
    BDropdown,
    BDropdownItem,
    SubmitButtons,
    BButton,
    ConfirmDialog,
    ColorPicker,
    IconDropdown,
    DeleteCfConfirmDialog
  },
  computed: {},
  methods: {
    ...mapActions(['addDisplayInitiativeField', 'attachCustomFields']),
    triggerOptionDeletion(index) {
      this.indexToDelete = index
      const option = this.dropdownOptions[index]
      if (this.isExistingOption(option)) {
        this.$refs.optionDeleteConfirmModal.show()
      } else {
        this.removeDropdownOption()
      }
    },
    getNextAvailableColor() {
      const availableColors = R.difference(COLORS, this.dropdownOptions.map(x => x.color))
      return availableColors[0] ?? COLORS[0]
    },
    getNewOption(color) {
      color = color || this.getNextAvailableColor()
      return { name: '', id: Math.random(), color }
    },
    addDropdownOption() {
      this.dropdownOptions.push(this.getNewOption())
      this.$nextTick(() => {
        this.$refs.option[this.$refs.option.length - 1].$el.focus()
      })
    },
    removeDropdownOption() {
      this.dropdownOptions = R.remove(this.indexToDelete, 1, this.dropdownOptions)
      if (this.errors.dropdownOptions) {
        this.errors.dropdownOptions = R.remove(this.indexToDelete, 1, this.errors.dropdownOptions)
      }
    },
    isExistingOption(option) {
      return option.id >= 1 // for new options, ids are < 1 since they generated using Math.random
    },
    async enableCustomField(customFieldDefinition) {
      // enable custom field for team/project
      try {
        if (!customFieldDefinition.projectId && this.enableAfterCreation) {
          // project-level CFs are by default attached to Projects
          await this.attachCustomFields({
            orgId: this.orgId,
            projectId: this.projectId,
            teamId: this.teamId,
            customFieldDefinitionIds: [customFieldDefinition.id]
          })
        }
        if (this.displayAfterCreation) {
          await this.addDisplayInitiativeField({
            orgId: this.orgId,
            projectId: this.projectId,
            teamId: this.teamId,
            payload: {
              customFieldDefinitionIds: [customFieldDefinition.id]
            }
          })
        }
      } catch (e) {
        console.warn('Error while attaching or adding to display list')
      }
    },
    async onSubmit() {
      try {
        this.isSubmitting = true
        const payload = {
          type: this.fieldType.value,
          name: this.fieldName,
          projectId: this.projectId,
          ...(this.fieldType.value === CUSTOM_FIELD_TYPE.NUMBER
            ? { formula: this.formula || '' }
            : {})
        }
        if (this.fieldType.value === CUSTOM_FIELD_TYPE.DROPDOWN) {
          payload.dropdownOptions = this.dropdownOptions.map(option => ({
            name: option.name?.trim(),
            color: option.color,
            ...(this.isExistingOption(option) ? { id: option.id } : {})
          }))
        }
        let customField, successMessage
        if (this.mode === 'CREATE') {
          if (this.addToOrg) {
            delete payload.projectId
          }
          const response = await service.client.post(urlManager.CFDefinitions(this.orgId), payload)
          customField = response.data
          successMessage = 'Custom field has been created successfully'
          await this.enableCustomField(customField)
          this.$emit('created', customField)
        } else {
          const response = await service.client.patch(
            urlManager.CFDefinitionsDetail({
              orgId: this.orgId,
              customFieldId: this.customField.id
            }),
            payload
          )
          successMessage = 'Changes have been saved successfully'
          if (this.customField.formula !== this.formula) {
            this.eventBusEmit(events.INITIATIVE_CUSTOM_FIELD_FORMULA_UPDATED, {
              customField: response.data
            })
          }
          this.$emit('saved', response.data)
        }
        this.eventBusEmit('ADD_TOAST', { message: successMessage })
        this.$store.dispatch('getOrgCustomFields', { orgId: this.orgId })
        this.errors = {}
        this.errorMessage = null
      } catch (e) {
        const errors = R.path(['response', 'data', 'detail'], e)
        if (typeof errors === 'object') {
          this.errors = errors
        } else {
          this.errorMessage = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        }
      }
      this.isSubmitting = false
    },
    async onDrop({ afterId, subjectId }) {
      const currentIndex = this.dropdownOptions.findIndex(R.propEq('id', +subjectId))
      let afterIndex = 0
      if (afterId) {
        afterIndex = this.dropdownOptions.findIndex(R.propEq('id', +afterId))
        if (afterIndex < currentIndex) {
          afterIndex++
        }
      }
      this.dropdownOptions = R.move(currentIndex, afterIndex, this.dropdownOptions)
    },
    enableOptionsDragAndDrop() {
      this.enableDragAndDrop({
        containers: [this.$refs.optionsContainer],
        onDrop: this.onDrop,
        handleName: 'gripper-icon',
        scrollingContainers: [[document.querySelector('.modal-body')]]
      })
    },
    getDropdownOptionsNameError(index) {
      return R.path([index, 'name', '0'], this.errors.dropdownOptions)
    },
    triggerDeleteConfirmation() {
      this.$refs.confirmModal.show()
    },
    addFormulaField() {
      this.showFormulaField = true
      this.$nextTick(() => this.$refs.formulaField.focus())
    }
  },
  mounted() {
    if (this.customField) {
      this.fieldType = this.fieldTypes.find(R.propEq('value', this.customField.type))
      this.fieldName = this.customField.name
      this.formula = this.customField.formula
      if (this.fieldType.value === CUSTOM_FIELD_TYPE.DROPDOWN) {
        this.enableOptionsDragAndDrop()
      }
    }
    this.listenToEventBus('SUBMIT_FORM', this.onSubmit)
  },
  watch: {
    fieldType(fieldType) {
      if (fieldType && fieldType.value === CUSTOM_FIELD_TYPE.DROPDOWN) {
        this.$nextTick(this.enableOptionsDragAndDrop)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/forms';
@import '~@/styles/drag';

/deep/ .dropdown-field,
/deep/ input {
  border-color: $tertiary;
}

/deep/ .dropdown-field {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.field {
  padding-bottom: 18px;
}

.back-nav {
  margin-left: -12px;
}

.field label {
  margin-bottom: 10px;
}

footer {
  padding: 12px 0 10px;
}

.dropdown-option-field:not(:last-child) {
  margin-bottom: 10px;
}

.gripper-icon {
  position: absolute;
  left: -25px;
  top: 5px;
  opacity: 0;
  cursor: move;
  transition: opacity 200ms ease-out;
}

.dropdown-option-field {
  position: relative;

  .options-dd {
    opacity: 0;
    transition: opacity 200ms ease-out;
  }

  &:hover .options-dd {
    opacity: 1;
  }

  &:hover .gripper-icon {
    opacity: 1;
  }
}

.error-message {
  color: $error;
}

.options-dd {
  margin-top: 6px;
  margin-left: 8px;
}
</style>
